// Données complètes pour la gestion des réunions du Comité TI
// 4 réunions trimestrielles simulées pour 2025 avec IA génération de résumés

export interface Participant {
  id: string;
  nom: string;
  prenom: string;
  role: 'Président' | 'Membre' | 'Invité' | 'Secrétaire';
  email: string;
  present: boolean;
}

export interface OrdreDuJourItem {
  id: string;
  numero: number;
  titre: string;
  description: string;
  dureeMinutes: number;
  responsable: string;
  statut: 'Non traité' | 'En discussion' | 'Complété' | 'Reporté';
}

export interface Note {
  id: string;
  type: 'generale' | 'decision' | 'enjeu' | 'risque';
  contenu: string;
  auteur: string;
  timestamp: string;
}

export interface Action {
  id: string;
  titre: string;
  description: string;
  responsable: string;
  echeance: string;
  priorite: 'Basse' | 'Moyenne' | 'Haute' | 'Critique';
  statut: 'À faire' | 'En cours' | 'Complété' | 'Bloqué';
}

export interface Reunion {
  id: string;
  titre: string;
  date: string; // Format: YYYY-MM-DD
  heure: string; // Format: HH:MM:SS
  dureeMinutes: number;
  lieu: string;
  objectifPrincipal: string;
  statut: 'Planifiée' | 'En cours' | 'Complétée';
  trimestre: 'Q1' | 'Q2' | 'Q3' | 'Q4';
  participants: Participant[];
  ordreDuJour: OrdreDuJourItem[];
  notes: Note[];
  actions: Action[];
  resumeGenere: boolean;
  resumeContenu?: string;
  verbatim?: string;
}

// ============================================
// PARTICIPANTS RÉCURRENTS
// ============================================

const participantsRecurrents: Participant[] = [
  {
    id: 'p1',
    nom: 'Bolduc',
    prenom: 'Sophie',
    role: 'Président',
    email: 'sophie.bolduc@strategiacorp.com',
    present: true
  },
  {
    id: 'p2',
    nom: 'Hains',
    prenom: 'Eric',
    role: 'Secrétaire',
    email: 'eric.hains@bzinc.ca',
    present: true
  },
  {
    id: 'p3',
    nom: 'Bourassa',
    prenom: 'Dany',
    role: 'Membre',
    email: 'dany.bourassa@bzinc.ca',
    present: true
  },
  {
    id: 'p4',
    nom: 'Gariepy',
    prenom: 'Maxime',
    role: 'Membre',
    email: 'maxime.gariepy@bzinc.ca',
    present: true
  },
  {
    id: 'p5',
    nom: 'Marquis',
    prenom: 'Francis',
    role: 'Membre',
    email: 'francis.marquis@bzinc.ca',
    present: true
  }
];

// ============================================
// RÉUNION Q1 2025 (PASSÉE, COMPLÉTÉE)
// ============================================

export const reunionQ1: Reunion = {
  id: 'reunion-q1-2025',
  titre: 'Comité TI - Pilotage Stratégique Q1',
  date: '2025-01-29',
  heure: '09:00:00',
  dureeMinutes: 120,
  lieu: 'Salle de conférence principale - Bureau Stratégia Corp',
  objectifPrincipal: 'Revue annuelle 2024, planification stratégique 2025, approbation budget TI et priorisation projets IA',
  statut: 'Complétée',
  trimestre: 'Q1',
  participants: participantsRecurrents.map(p => ({ ...p, present: true })),
  
  ordreDuJour: [
    {
      id: 'q1-odj-1',
      numero: 1,
      titre: 'Revue performance TI 2024',
      description: 'Présentation des KPIs annuels, taux de satisfaction client (84%), projets livrés (12/14), budget respecté (96%)',
      dureeMinutes: 30,
      responsable: 'Eric Hains',
      statut: 'Complété'
    },
    {
      id: 'q1-odj-2',
      numero: 2,
      titre: 'Approbation budget TI 2025',
      description: 'Validation budget annuel 780k$ (+14.7% vs 2024), répartition par pilier, investissements IA 120k$',
      dureeMinutes: 25,
      responsable: 'Sophie Bolduc',
      statut: 'Complété'
    },
    {
      id: 'q1-odj-3',
      numero: 3,
      titre: 'Stratégie IA & Innovation 2025',
      description: 'Feuille de route IA : automatisation rapports (Q1), chatbot support (Q2), prédiction inventaire (Q3)',
      dureeMinutes: 35,
      responsable: 'Dany Bourassa',
      statut: 'Complété'
    },
    {
      id: 'q1-odj-4',
      numero: 4,
      titre: 'Cybersécurité - Conformité NIST 2.0',
      description: 'État conformité actuelle 68%, plan remédiation 18 contrôles, audit externe prévu Q2',
      dureeMinutes: 20,
      responsable: 'Francis Marquis',
      statut: 'Complété'
    },
    {
      id: 'q1-odj-5',
      numero: 5,
      titre: 'Priorisation portefeuille projets',
      description: 'Revue et priorisation 18 projets demandés, validation top 8 projets stratégiques 2025',
      dureeMinutes: 10,
      responsable: 'Maxime Gariepy',
      statut: 'Complété'
    }
  ],
  
  notes: [
    {
      id: 'q1-note-1',
      type: 'generale',
      contenu: 'Performance TI 2024 très satisfaisante - taux satisfaction client atteint 84% (cible 80%). 12 projets livrés sur 14 planifiés. Budget respecté à 96%.',
      auteur: 'Eric Hains',
      timestamp: '2025-01-29T09:15:00'
    },
    {
      id: 'q1-note-2',
      type: 'decision',
      contenu: 'DÉCISION: Budget TI 2025 approuvé à 780k$ unanimement. Augmentation de 14.7% justifiée par investissements IA et cybersécurité.',
      auteur: 'Sophie Bolduc',
      timestamp: '2025-01-29T09:45:00'
    },
    {
      id: 'q1-note-3',
      type: 'decision',
      contenu: 'DÉCISION: Feuille de route IA 2025 validée avec 3 projets pilotes. Budget alloué 120k$. Dany responsable livraison.',
      auteur: 'Sophie Bolduc',
      timestamp: '2025-01-29T10:05:00'
    },
    {
      id: 'q1-note-4',
      type: 'risque',
      contenu: 'RISQUE: Conformité NIST actuelle à 68% expose Stratégia Corp à risques audit. 18 contrôles critiques manquants identifiés.',
      auteur: 'Francis Marquis',
      timestamp: '2025-01-29T10:25:00'
    },
    {
      id: 'q1-note-5',
      type: 'decision',
      contenu: 'DÉCISION: Plan remédiation cybersécurité approuvé. Budget additionnel 35k$ autorisé. Audit externe prévu mai 2025.',
      auteur: 'Sophie Bolduc',
      timestamp: '2025-01-29T10:35:00'
    },
    {
      id: 'q1-note-6',
      type: 'generale',
      contenu: 'Priorisation portefeuille: 18 projets analysés via matrice valeur/risque. Top 8 retenus alignés stratégie Stratégia Corp.',
      auteur: 'Maxime Gariepy',
      timestamp: '2025-01-29T10:50:00'
    }
  ],
  
  actions: [
    {
      id: 'q1-action-1',
      titre: 'Lancer projet automatisation rapports IA',
      description: 'Démarrer POC automatisation génération rapports mensuels via IA (12 rapports identifiés)',
      responsable: 'Dany Bourassa',
      echeance: '2025-03-31',
      priorite: 'Haute',
      statut: 'Complété'
    },
    {
      id: 'q1-action-2',
      titre: 'Planifier audit cybersécurité externe',
      description: 'Sélectionner auditeur externe, calendrier audit mai 2025, préparer documentation',
      responsable: 'Francis Marquis',
      echeance: '2025-02-28',
      priorite: 'Haute',
      statut: 'Complété'
    },
    {
      id: 'q1-action-3',
      titre: 'Communiquer roadmap TI 2025 à organisation',
      description: 'Présentation Town Hall, newsletter TI, sessions départements, FAQ',
      responsable: 'Eric Hains',
      echeance: '2025-02-15',
      priorite: 'Moyenne',
      statut: 'Complété'
    }
  ],
  
  resumeGenere: true,
  resumeContenu: `**RÉSUMÉ PROFESSIONNEL - COMITÉ TI Q1 2025**

**Date:** 29 janvier 2025 | **Durée:** 2h00 | **Participants:** 5 personnes

**CONTEXTE ET OBJECTIFS**
Première réunion du Comité TI 2025 dédiée à la revue annuelle 2024 et la planification stratégique de l'année. Objectif : valider le budget, approuver la feuille de route IA et prioriser les investissements technologiques.

**DÉCISIONS MAJEURES**

1. **Budget TI 2025 Approuvé** 
   - Montant : 780k$ (+14.7% vs 2024)
   - Décidé par : Sophie Bolduc (Présidente)
   - Répartition : IA (120k$), Cybersécurité (35k$ additionnel), Projets (385k$), Opérations (240k$)

2. **Feuille de Route IA 2025 Validée**
   - Décidé par : Sophie Bolduc sur recommandation Dany Bourassa
   - 3 projets pilotes : Automatisation rapports (Q1), Chatbot support (Q2), Prédiction inventaire (Q3)
   - Budget alloué : 120k$

3. **Plan Remédiation Cybersécurité Approuvé**
   - Décidé par : Sophie Bolduc sur alerte Francis Marquis
   - 18 contrôles NIST critiques à remédier
   - Budget additionnel : 35k$
   - Audit externe planifié : Mai 2025

**ENJEUX ET RISQUES IDENTIFIÉS**

⚠️ **RISQUE CRITIQUE - Conformité NIST 2.0**
- Niveau actuel : 68% (cible : 85%+)
- Impact : Risques audit, exposition sécurité, réputation
- Mitigation : Plan remédiation 6 mois, budget additionnel approuvé

**ACTIONS DÉCIDÉES**

| Action | Responsable | Échéance | Priorité |
|--------|-------------|----------|----------|
| Lancer POC automatisation rapports IA | Dany Bourassa | 31 mars 2025 | Haute |
| Planifier audit cybersécurité externe | Francis Marquis | 28 février 2025 | Haute |
| Communiquer roadmap TI 2025 | Eric Hains | 15 février 2025 | Moyenne |

**PROCHAINES ÉTAPES**
- Prochaine réunion : Q2 2025 (29 avril)
- Suivi : Performance projets IA Q1, avancement remédiation cyber, résultats audit
- Communication : Town Hall TI février, newsletter mensuelle

**PERFORMANCE TI 2024 (RÉFÉRENCE)**
- ✅ Satisfaction client : 84% (cible 80%)
- ✅ Projets livrés : 12/14 (86%)
- ✅ Budget respecté : 96%

---
*Résumé généré par IA PRIsME • Validé par Eric Hains • Conforme NIST AI Standard*`,
  
  verbatim: `[09:00] Sophie Bolduc : Bonjour à tous, bienvenue à notre premier comité TI 2025. Eric, peux-tu nous faire le point sur 2024?

[09:05] Eric Hains : Avec plaisir Sophie. 2024 a été une excellente année. Satisfaction client à 84%, on a livré 12 projets sur 14, budget respecté à 96%. On peut être fiers.

[09:10] Sophie : Merci Eric. Passons au budget 2025. On parle de 780k$, c'est 15% d'augmentation. Dany, justification?

[09:15] Dany Bourassa : Oui Sophie. Cette augmentation est surtout pour l'IA - 120k$ budgétés pour 3 pilotes. Automatisation rapports, chatbot, prédiction inventaire. ROI attendu 300k$/an.

[09:25] Maxime Gariepy : Je confirme les chiffres Dany. J'ai fait l'analyse. Les gains sont conservateurs.

[09:30] Sophie : Ok, je propose qu'on vote. Qui est pour? ... Unanime. Budget approuvé.

[09:45] Francis Marquis : Sophie, un point critique. Notre conformité NIST est à 68%. On a 18 contrôles manquants. Risque audit majeur.

[09:50] Sophie : C'est sérieux Francis. Que proposes-tu?

[09:52] Francis : Plan remédiation 6 mois, 35k$ additionnel, audit externe mai.

[09:55] Sophie : Approuvé. On ne peut pas jouer avec la sécurité. Eric, tu coordonnes avec Francis?

[10:00] Eric : Absolument Sophie.

[10:15] Maxime : Pour les projets, j'ai priorisé les 18 demandes. Top 8 retenus alignés stratégie.

[10:45] Sophie : Parfait. Prochaine réunion avril. Merci à tous!`
};

// ============================================
// RÉUNION Q2 2025 (PASSÉE, EN COURS)
// ============================================

export const reunionQ2: Reunion = {
  id: 'reunion-q2-2025',
  titre: 'Comité TI - Pilotage Stratégique Q2',
  date: '2025-04-29',
  heure: '14:00:00',
  dureeMinutes: 90,
  lieu: 'Teams - Réunion virtuelle',
  objectifPrincipal: 'Suivi projets IA Q1, résultats audit cybersécurité, lancement initiatives Q2 et revue KPIs trimestriels',
  statut: 'En cours',
  trimestre: 'Q2',
  participants: participantsRecurrents.map((p, idx) => ({ 
    ...p, 
    present: idx !== 4 // Francis absent
  })),
  
  ordreDuJour: [
    {
      id: 'q2-odj-1',
      numero: 1,
      titre: 'Bilan projets IA Q1',
      description: 'Résultats POC automatisation rapports : 85% succès, ROI projeté 95k$/an, décision GO/NO-GO phase 2',
      dureeMinutes: 25,
      responsable: 'Dany Bourassa',
      statut: 'Complété'
    },
    {
      id: 'q2-odj-2',
      numero: 2,
      titre: 'Résultats audit cybersécurité',
      description: 'Rapport auditeur externe : conformité NIST passée de 68% à 79%, 8 contrôles résolus, 10 en cours',
      dureeMinutes: 20,
      responsable: 'Francis Marquis',
      statut: 'Reporté'
    },
    {
      id: 'q2-odj-3',
      numero: 3,
      titre: 'Lancement chatbot support Q2',
      description: 'Validation cahier charges, sélection fournisseur (3 options), budget 45k$, timeline 12 semaines',
      dureeMinutes: 25,
      responsable: 'Dany Bourassa',
      statut: 'En discussion'
    },
    {
      id: 'q2-odj-4',
      numero: 4,
      titre: 'Revue KPIs TI Q1 2025',
      description: 'Performance : MTTR 4.2h (cible 4h), SLA 92% (cible 90%), satisfaction 86%, budget 98%',
      dureeMinutes: 15,
      responsable: 'Eric Hains',
      statut: 'Complété'
    },
    {
      id: 'q2-odj-5',
      numero: 5,
      titre: 'Roadmap Cloud Migration',
      description: 'Plan migration 15 applications vers Azure, phases Q3-Q4, budget 125k$, risques identifiés',
      dureeMinutes: 5,
      responsable: 'Maxime Gariepy',
      statut: 'Non traité'
    }
  ],
  
  notes: [
    {
      id: 'q2-note-1',
      type: 'generale',
      contenu: 'POC automatisation rapports IA Q1 très réussi : 85% succès sur 12 rapports testés. Gain temps estimé 285h/an.',
      auteur: 'Dany Bourassa',
      timestamp: '2025-04-29T14:10:00'
    },
    {
      id: 'q2-note-2',
      type: 'decision',
      contenu: 'DÉCISION: Phase 2 automatisation rapports IA approuvée. Déploiement production 8 rapports prioritaires. Budget 32k$.',
      auteur: 'Sophie Bolduc',
      timestamp: '2025-04-29T14:25:00'
    },
    {
      id: 'q2-note-3',
      type: 'enjeu',
      contenu: 'ENJEU: Francis absent pour rapport audit cyber. Eric présente à sa place mais détails techniques manquants.',
      auteur: 'Eric Hains',
      timestamp: '2025-04-29T14:30:00'
    },
    {
      id: 'q2-note-4',
      type: 'generale',
      contenu: 'Audit cyber: progression positive 68% → 79%. 8 contrôles critiques résolus Q1. 10 contrôles restants en cours remédiation.',
      auteur: 'Eric Hains',
      timestamp: '2025-04-29T14:40:00'
    },
    {
      id: 'q2-note-5',
      type: 'generale',
      contenu: 'Chatbot support: 3 fournisseurs évalués (IBM Watson 65k$, Azure Bot 45k$, Custom 38k$). Discussion en cours.',
      auteur: 'Dany Bourassa',
      timestamp: '2025-04-29T14:55:00'
    },
    {
      id: 'q2-note-6',
      type: 'risque',
      contenu: 'RISQUE: Fournisseur custom chatbot moins cher mais support limité et pas de garantie SLA 99.9%.',
      auteur: 'Maxime Gariepy',
      timestamp: '2025-04-29T15:05:00'
    }
  ],
  
  actions: [
    {
      id: 'q2-action-1',
      titre: 'Finaliser sélection fournisseur chatbot',
      description: 'Comparaison détaillée 3 fournisseurs, validation technique, négociation contrat, décision finale',
      responsable: 'Dany Bourassa',
      echeance: '2025-05-15',
      priorite: 'Haute',
      statut: 'En cours'
    },
    {
      id: 'q2-action-2',
      titre: 'Compléter rapport audit cybersécurité',
      description: 'Francis doit présenter rapport détaillé audit externe avec plan remédiation 10 contrôles restants',
      responsable: 'Francis Marquis',
      echeance: '2025-05-10',
      priorite: 'Critique',
      statut: 'Bloqué'
    },
    {
      id: 'q2-action-3',
      titre: 'Déployer automatisation rapports Phase 2',
      description: '8 rapports prioritaires en production, formation utilisateurs, documentation, monitoring',
      responsable: 'Dany Bourassa',
      echeance: '2025-06-30',
      priorite: 'Haute',
      statut: 'À faire'
    },
    {
      id: 'q2-action-4',
      titre: 'Préparer cahier charges migration cloud',
      description: 'Analyse 15 applications, dépendances, architecture cible Azure, estimation coûts, planning',
      responsable: 'Maxime Gariepy',
      echeance: '2025-05-31',
      priorite: 'Haute',
      statut: 'À faire'
    },
    {
      id: 'q2-action-5',
      titre: 'Organiser formation IA équipe TI',
      description: 'Sessions formation IA générative pour équipe TI (8 personnes), certification Azure AI',
      responsable: 'Dany Bourassa',
      echeance: '2025-06-15',
      priorite: 'Moyenne',
      statut: 'À faire'
    }
  ],
  
  resumeGenere: false,
  resumeContenu: undefined,
  verbatim: undefined
};

// ============================================
// RÉUNION Q3 2025 (FUTURE)
// ============================================

export const reunionQ3: Reunion = {
  id: 'reunion-q3-2025',
  titre: 'Comité TI - Pilotage Stratégique Q3',
  date: '2025-07-29',
  heure: '09:30:00',
  dureeMinutes: 120,
  lieu: 'Salle de conférence principale - Bureau Stratégia Corp',
  objectifPrincipal: 'Revue mi-année 2025, validation migration cloud, lancement prédiction inventaire IA et préparation budget 2026',
  statut: 'Planifiée',
  trimestre: 'Q3',
  participants: participantsRecurrents,
  
  ordreDuJour: [
    {
      id: 'q3-odj-1',
      numero: 1,
      titre: 'Bilan mi-année TI 2025',
      description: 'Revue performance H1: projets, budget, KPIs, satisfaction client, analyse écarts vs objectifs',
      dureeMinutes: 30,
      responsable: 'Eric Hains',
      statut: 'Non traité'
    },
    {
      id: 'q3-odj-2',
      numero: 2,
      titre: 'Résultats chatbot support Q2',
      description: 'Métriques post-déploiement: taux résolution, satisfaction, ROI, recommandations améliorations',
      dureeMinutes: 20,
      responsable: 'Dany Bourassa',
      statut: 'Non traité'
    },
    {
      id: 'q3-odj-3',
      numero: 3,
      titre: 'GO/NO-GO Migration Cloud',
      description: 'Validation finale cahier charges, architecture, coûts 125k$, planning Q3-Q4, risques',
      dureeMinutes: 35,
      responsable: 'Maxime Gariepy',
      statut: 'Non traité'
    },
    {
      id: 'q3-odj-4',
      numero: 4,
      titre: 'Lancement projet prédiction inventaire IA',
      description: 'Présentation POC, données historiques, algorithmes ML, KPIs cibles, budget 55k$',
      dureeMinutes: 25,
      responsable: 'Dany Bourassa',
      statut: 'Non traité'
    },
    {
      id: 'q3-odj-5',
      numero: 5,
      titre: 'Préparation budget TI 2026',
      description: 'Orientations stratégiques 2026, enveloppe budgétaire préliminaire, priorisation investissements',
      dureeMinutes: 10,
      responsable: 'Sophie Bolduc',
      statut: 'Non traité'
    }
  ],
  
  notes: [],
  actions: [],
  resumeGenere: false,
  resumeContenu: undefined,
  verbatim: undefined
};

// ============================================
// RÉUNION Q4 2025 (FUTURE)
// ============================================

export const reunionQ4: Reunion = {
  id: 'reunion-q4-2025',
  titre: 'Comité TI - Pilotage Stratégique Q4',
  date: '2025-10-29',
  heure: '14:00:00',
  dureeMinutes: 150,
  lieu: 'Teams - Réunion virtuelle',
  objectifPrincipal: 'Bilan annuel 2025, approbation budget TI 2026, revue stratégique 3 ans et planification innovations 2026',
  statut: 'Planifiée',
  trimestre: 'Q4',
  participants: participantsRecurrents,
  
  ordreDuJour: [
    {
      id: 'q4-odj-1',
      numero: 1,
      titre: 'Bilan annuel TI 2025',
      description: 'Revue complète année: tous projets livrés, budget final, KPIs annuels, satisfaction, leçons apprises',
      dureeMinutes: 40,
      responsable: 'Eric Hains',
      statut: 'Non traité'
    },
    {
      id: 'q4-odj-2',
      numero: 2,
      titre: 'ROI initiatives IA 2025',
      description: 'Bilan financier 3 projets IA: gains réels vs projetés, économies, productivité, recommandations 2026',
      dureeMinutes: 30,
      responsable: 'Dany Bourassa',
      statut: 'Non traité'
    },
    {
      id: 'q4-odj-3',
      numero: 3,
      titre: 'Résultats migration cloud',
      description: 'Post-mortem migration 15 applications, performance, coûts réels, incidents, optimisations',
      dureeMinutes: 25,
      responsable: 'Maxime Gariepy',
      statut: 'Non traité'
    },
    {
      id: 'q4-odj-4',
      numero: 4,
      titre: 'Approbation budget TI 2026',
      description: 'Présentation budget détaillé 2026, investissements stratégiques, vote formel comité',
      dureeMinutes: 35,
      responsable: 'Sophie Bolduc',
      statut: 'Non traité'
    },
    {
      id: 'q4-odj-5',
      numero: 5,
      titre: 'Vision stratégique TI 2026-2028',
      description: 'Orientations 3 ans: IA avancée, modernisation, cybersécurité, talent, gouvernance',
      dureeMinutes: 20,
      responsable: 'Sophie Bolduc',
      statut: 'Non traité'
    }
  ],
  
  notes: [],
  actions: [],
  resumeGenere: false,
  resumeContenu: undefined,
  verbatim: undefined
};

// ============================================
// EXPORT GLOBAL
// ============================================

export const toutesLesReunions: Reunion[] = [
  reunionQ1,
  reunionQ2,
  reunionQ3,
  reunionQ4
];

// Helper pour générer un résumé IA (mockup)
export const genererResumeIA = (reunion: Reunion): string => {
  const notesGenerales = reunion.notes.filter(n => n.type === 'generale');
  const decisions = reunion.notes.filter(n => n.type === 'decision');
  const enjeux = reunion.notes.filter(n => n.type === 'enjeu');
  const risques = reunion.notes.filter(n => n.type === 'risque');
  
  const actionsHaute = reunion.actions.filter(a => a.priorite === 'Haute' || a.priorite === 'Critique');
  
  return `**RÉSUMÉ PROFESSIONNEL - ${reunion.titre.toUpperCase()}**

**Date:** ${new Date(reunion.date).toLocaleDateString('fr-FR', { dateStyle: 'long' })} | **Durée:** ${reunion.dureeMinutes}min | **Participants:** ${reunion.participants.filter(p => p.present).length} personnes

**CONTEXTE ET OBJECTIFS**
${reunion.objectifPrincipal}

**NOTES PRINCIPALES**
${notesGenerales.map(n => `• ${n.contenu.replace(/[A-Z]+:/g, '')}`).join('\n') || '• Aucune note générale enregistrée'}

**DÉCISIONS PRISES**
${decisions.map((d, idx) => `${idx + 1}. ${d.contenu.replace('DÉCISION:', '').trim()}`).join('\n') || '• Aucune décision formelle prise'}

**ENJEUX ET RISQUES IDENTIFIÉS**
${enjeux.length > 0 ? enjeux.map(e => `⚠️ ${e.contenu.replace('ENJEU:', '').trim()}`).join('\n') : ''}${risques.length > 0 ? '\n' + risques.map(r => `🎯 RISQUE: ${r.contenu.replace('RISQUE:', '').trim()}`).join('\n') : ''}${enjeux.length === 0 && risques.length === 0 ? '• Aucun enjeu ou risque critique identifié' : ''}

**ACTIONS PRIORITAIRES DÉCIDÉES**

| Action | Responsable | Échéance | Priorité | Statut |
|--------|-------------|----------|----------|--------|
${actionsHaute.map(a => `| ${a.titre} | ${a.responsable} | ${new Date(a.echeance).toLocaleDateString('fr-FR')} | ${a.priorite} | ${a.statut} |`).join('\n')}

**ORDRE DU JOUR TRAITÉ**
${reunion.ordreDuJour.map(odj => `${odj.numero}. ${odj.titre} (${odj.dureeMinutes}min) - ${odj.statut}`).join('\n')}

**PROCHAINES ÉTAPES**
• Suivi actions décidées lors de la prochaine réunion
• ${reunion.trimestre === 'Q4' ? 'Planification stratégique année suivante' : `Prochaine réunion: ${reunion.trimestre === 'Q1' ? 'Q2' : reunion.trimestre === 'Q2' ? 'Q3' : 'Q4'} ${reunion.date.split('-')[0]}`}
• Communication résumé aux parties prenantes

---
*Résumé généré par IA PRIsME • Conforme NIST AI Standard • ${new Date().toLocaleString('fr-FR')}*`;
};

